<template>
  <div>
    <FullscreenModal :show="show" @close="show = false">
      <SolutionPage
        :module-spec-params="moduleExtraSpecs()"
        :driver="solution.driver"
        :module="solution.module"
        :sensor="solution.sensor"
        :bridge="solution.bridge"
        :multiLengthModules="solution.multiLengthModules"
        :virtualModule="solution.virtualModule"
        :emergencyDrivers="solution.emergencyDrivers"
        :inputValues="inputValues"
        :filename="solution.filename"
        :userComment="solution.userComment"
        :isLoggedIn="isLoggedIn"
        :region="solution.region"
        :isWhitelistedSolution="isWhitelistedSolution"
        :maintenanceMode="maintenanceMode"
        @inputValuesChange="inputValuesChange"
        @filenameChange="filenameChange"
        @userCommentChange="userCommentChange"
      ></SolutionPage>
      <template v-slot:stickyfootercontent>
        <solution-page-sticky-footer
          :region="solution.region"
          :isLoggedIn="isLoggedIn"
          :isWhitelistedSolution="isWhitelistedSolution"
          @download="generatePdf(true)"
          @save="openSaveModal"
          @purchaseSamples="toPurchaseSamples"
          @selectAccessoriesClicked="openAddonModal"
          @requestValidation="requestValidation"
        />
      </template>
    </FullscreenModal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import settings from "@/service/Settings";
import SaveSolutionModal from "@/components/modals/SaveSolutionModal";
import AddonModal from "@/components/modals/AddonModal";
import RequestValidationModal from "@/components/modals/RequestValidationModal";
import {
  MAX_VF_AND_MIN_LUM_FLUX,
  THERMAL_POWER_PER_LED_PACKAGE,
  RTH_MODULE_CALCULATIONS,
} from "@/service/Constants";
import { assign } from "lodash";

export default {
  name: "SolutionModal",
  modals: { SaveSolutionModal, RequestValidationModal, AddonModal },
  data() {
    return {
      show: true,
    };
  },
  props: ["solution"],
  computed: {
    ...mapState("brain", ["region", "maintenanceMode"]),
    ...mapGetters("brain/auth", ["aclFeatures", "isLoggedIn"]),
    ...mapGetters("brain/drivers", [
      "isOddCombination",
      "isWhitelistedSolution",
    ]),
    inputValues() {
      const a = this.solution.inputValues;
      const b = { lumenOutputRatio: this.lor };

      const assigned = assign({}, a, b);
      return assigned;
    },
    lor: {
      get() {
        if (
          !this.solution.module ||
          this.solution.module.calculationParameters == null
        ) {
          return 100;
        }
        return Math.round(
          this.solution.module.calculationParameters.luminaireLor * 100
        );
      },
      set(val) {
        this.$store.commit("brain/solutions/setLor", val / 100);
      },
    },
  },
  methods: {
    moduleExtraSpecs() {
      let result = [];
      this.canI(MAX_VF_AND_MIN_LUM_FLUX) &&
        result.push("maximumForwardVoltage", "minLuminousFluxPerModule");
      this.canI(THERMAL_POWER_PER_LED_PACKAGE) &&
        result.push("thermalPowerPerLedPackage");
      this.canI(RTH_MODULE_CALCULATIONS) && result.push("rthModuleCalculation");
      return result;
    },

    toPurchaseSamples() {
      const formatNc12 = (nc12) => {
        return (
          nc12
            .split("_")
            .filter((n) => n.length === 12)
            .join("+") || nc12.substring(0, 12) // If no valid nc12 can be found, just truncate the whole nc12
        );
      };

      let queryParams = "?q=";
      if (this.solution.driver) {
        queryParams += "+" + formatNc12(this.solution.driver.nc12);
      }
      if (this.solution.module && this.solution.module.nc12) {
        queryParams += "+" + formatNc12(this.solution.module.nc12);
      }
      if (this.solution.emergencyDrivers.length) {
        this.solution.emergencyDrivers.forEach((emergencyDriver) => {
          queryParams += "+" + formatNc12(emergencyDriver.nc12);
        });
      }
      if (this.solution.multiLengthModules.length) {
        this.solution.multiLengthModules.forEach((multiLengthModule) => {
          queryParams += "+" + formatNc12(multiLengthModule.nc12);
        });
      }
      if (this.solution.sensor) {
        queryParams += "+" + formatNc12(this.solution.sensor.nc12);
      }
      if (this.solution.bridge) {
        queryParams += "+" + formatNc12(this.solution.bridge.nc12);
      }
      window.open(
        settings.EMEA_OEMWEBSHOP_URL + "/catalogsearch/result/" + queryParams,
        "_blank"
      );
    },

    openSaveModal() {
      if (!this.isLoggedIn) {
        this.$snotify.error(
          "Saving solutions is only available for logged in users"
        );
      } else {
        this.$modalRouter.push({
          name: "SaveSolutionModal",
          props: { show: true, initialFilename: this.solution.filename },
          model: "show",
          on: {
            onSaveSolution: this.onSaveSolution,
          },
        });
      }
    },

    onSaveSolution(filename) {
      this.$store.commit("brain/solutions/setFilename", filename);
      this.$store
        .dispatch("brain/solutions/saveSolution", filename)
        .then(() => {
          this.$snotify.success("Solution saved succesfully");
        })
        .catch((error) => {
          if (
            error.response["data"]["message"] === "Maximum solutions exceeded"
          ) {
            this.$snotify.error(
              "The maximum number of files that is supported has been reached."
            );
          } else {
            this.$snotify.error("Error saving solution");
          }
        });
    },

    openAddonModal() {
      this.$modalRouter.push({
        name: "AddonModal",
        props: { show: true },
        model: "show",
        on: {
          onOpenEmergencyDrivers: this.onOpenEmergencyDrivers,
        },
      });
    },

    onOpenEmergencyDrivers() {
      if (this.region === "na" || this.region === "emea") {
        this.close();
        if (this.$router.currentRoute.name !== "emergencyDriver") {
          this.$router.push({ name: "emergencyDriver" });
        }
      } else {
        this.$store.dispatch(
          "brain/ui/showToast",
          {
            type: "success",
            message: "There are no accessories available yet",
          },
          { root: true }
        );
      }
    },

    generatePdf(download) {
      if (!this.canI("SHOW_DOWNLOAD_PDF_BUTTON")) {
        this.$snotify.error(
          "You do not have sufficient permissions to download a PDF of your solution"
        );
      } else {
        this.$store.dispatch("brain/solutions/download", download);
      }
    },

    inputValuesChange(inputValues) {
      this.$store.commit("brain/solutions/setInputValues", inputValues);
      this.lor = inputValues.lumenOutputRatio;
    },

    filenameChange(filename) {
      this.$store.commit("brain/solutions/setFilename", filename);
    },

    userCommentChange(userComment) {
      this.$store.commit("brain/solutions/setUserComment", userComment);
    },

    close() {
      this.show = false;
    },

    requestValidation() {
      this.$modalRouter.push({
        name: "RequestValidationModal",
        props: {
          show: true,
          driver: this.solution.driver,
          module: this.solution.module,
        },
        model: "show",
      });
    },
  },
};
</script>
